/**
 * Brief Setup Page – Light workspace theme
 * User selects brief type (short/detailed) and business type (B2B/B2C)
 * before filling out the multi-step brief form.
 */
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import {
	ArrowLeft,
	FileText,
	BookOpen,
	Building2,
	Users,
	ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/hooks/use-translations";

type BriefType = "short" | "detailed" | null;
type BusinessType = "b2b" | "b2c" | null;

export function BriefSetupPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const projectName = searchParams.get("name") || "Untitled Project";
	const chatId = searchParams.get("chatId") || "";
	const t = useTranslations("brief");

	const [briefType, setBriefType] = useState<BriefType>(null);
	const [businessType, setBusinessType] = useState<BusinessType>(null);

	const handleContinue = () => {
		if (!briefType || !businessType) return;
		navigate(
			`/projects/new/brief?name=${encodeURIComponent(projectName)}&type=${briefType}&business=${businessType}&chatId=${encodeURIComponent(chatId)}`,
		);
	};

	return (
		<div className="p-6 max-w-3xl mx-auto space-y-8">
			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: -10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.2 }}
			>
				<Button
					variant="ghost"
					size="sm"
					onClick={() => navigate("/projects")}
					className="mb-3 -ml-2"
				>
					<ArrowLeft size={16} />
					{t("setup.backToProjects")}
				</Button>

				<h1 className="text-2xl font-semibold text-text-primary">{projectName}</h1>
				<p className="mt-1 text-sm text-text-tertiary">{t("setup.setupDesc")}</p>
			</motion.div>

			{/* Step 1: Brief Type */}
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ delay: 0.1 }}
			>
				<h2 className="text-lg font-medium text-text-primary mb-1">
					{t("setup.chooseBriefType")}
				</h2>
				<p className="text-sm text-text-tertiary mb-4">{t("setup.briefTypeDesc")}</p>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div
						className={cn(
							"rounded-2xl border cursor-pointer transition-all hover:shadow-lg",
							briefType === "short"
								? "border-brand-700 bg-brand-50"
								: "border-border-subtle bg-bg-surface hover:border-brand-700 hover:bg-brand-50",
						)}
						onClick={() => setBriefType("short")}
					>
						<div className="p-5">
							<div className="flex items-start gap-3">
								<div
									className={cn(
										"p-2.5 rounded-lg transition-colors",
										briefType === "short"
											? "bg-brand-400 text-text-on-brand"
											: "bg-brand-100 text-text-primary",
									)}
								>
									<FileText size={22} />
								</div>
								<div>
									<h3 className="font-medium text-text-primary">{t("setup.short")}</h3>
									<p className="text-sm text-text-tertiary mt-1">
										{t("setup.shortDesc")}
									</p>
									<p className="text-xs text-text-tertiary mt-2">
										{t("setup.shortTime")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div
						className={cn(
							"rounded-2xl border cursor-pointer transition-all hover:shadow-lg",
							briefType === "detailed"
								? "border-brand-700 bg-brand-50"
								: "border-border-subtle bg-bg-surface hover:border-brand-700 hover:bg-brand-50",
						)}
						onClick={() => setBriefType("detailed")}
					>
						<div className="p-5">
							<div className="flex items-start gap-3">
								<div
									className={cn(
										"p-2.5 rounded-lg transition-colors",
										briefType === "detailed"
											? "bg-brand-400 text-text-on-brand"
											: "bg-brand-100 text-text-primary",
									)}
								>
									<BookOpen size={22} />
								</div>
								<div>
									<h3 className="font-medium text-text-primary">
										{t("setup.detailed")}
									</h3>
									<p className="text-sm text-text-tertiary mt-1">
										{t("setup.detailedDesc")}
									</p>
									<p className="text-xs text-text-tertiary mt-2">
										{t("setup.detailedTime")}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</motion.div>

			{/* Step 2: Business Type */}
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ delay: 0.15 }}
			>
				<h2 className="text-lg font-medium text-text-primary mb-1">
					{t("setup.businessType")}
				</h2>
				<p className="text-sm text-text-tertiary mb-4">
					{t("setup.businessTypeDesc")}
				</p>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div
						className={cn(
							"rounded-2xl border cursor-pointer transition-all hover:shadow-lg",
							businessType === "b2b"
								? "border-brand-700 bg-brand-50"
								: "border-border-subtle bg-bg-surface hover:border-brand-700 hover:bg-brand-50",
						)}
						onClick={() => setBusinessType("b2b")}
					>
						<div className="p-5">
							<div className="flex items-start gap-3">
								<div
									className={cn(
										"p-2.5 rounded-lg transition-colors",
										businessType === "b2b"
											? "bg-brand-400 text-text-on-brand"
											: "bg-brand-100 text-text-primary",
									)}
								>
									<Building2 size={22} />
								</div>
								<div>
									<h3 className="font-medium text-text-primary">{t("setup.b2b")}</h3>
									<p className="text-sm text-text-tertiary mt-1">
										{t("setup.b2bDesc")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div
						className={cn(
							"rounded-2xl border cursor-pointer transition-all hover:shadow-lg",
							businessType === "b2c"
								? "border-brand-700 bg-brand-50"
								: "border-border-subtle bg-bg-surface hover:border-brand-700 hover:bg-brand-50",
						)}
						onClick={() => setBusinessType("b2c")}
					>
						<div className="p-5">
							<div className="flex items-start gap-3">
								<div
									className={cn(
										"p-2.5 rounded-lg transition-colors",
										businessType === "b2c"
											? "bg-brand-400 text-text-on-brand"
											: "bg-brand-100 text-text-primary",
									)}
								>
									<Users size={22} />
								</div>
								<div>
									<h3 className="font-medium text-text-primary">{t("setup.b2c")}</h3>
									<p className="text-sm text-text-tertiary mt-1">
										{t("setup.b2cDesc")}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</motion.div>

			{/* Continue Button */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 0.2 }}
				className="flex justify-end pt-2"
			>
				<Button
					size="lg"
					onClick={handleContinue}
					disabled={!briefType || !businessType}
				>
					{t("setup.continue")}
					<ArrowRight size={16} />
				</Button>
			</motion.div>
		</div>
	);
}
